import { useEffect, useState, useCallback, useRef } from "react";
import { MdSearch, MdAddBox, MdMoreVert, MdRotateRight } from "react-icons/md";
import TablePageLayout from "components/TablePageLayout";
import Pagination from "components/Pagination";
import Modal from "components/common/Modal";
import ConfirmModal from "components/common/ConfirmModal";
import Alert from "components/common/Alert";
import CustomButton from "components/CustomButton";
import { InputField, SelectField } from "components/FormFields";

import BulkNotificationForm from "components/bulkNotification/BulkNotificationForm";

import {
  getNotifications,
  createNotification,
  resendNotification,
} from "api/pushNotification.api";

// --- TABLE ROW SUB-COMPONENT ---
function NotificationRow({
  item,
  index,
  startIndex,
  onAskResend,
  openDropdownId,
  setOpenDropdownId,
  openUpward,
  setOpenUpward,
  dropdownRef,
}) {
  const isMenuOpen = openDropdownId === item._id;

  const handleToggleMenu = (e) => {
    e.stopPropagation();
    if (isMenuOpen) {
      setOpenDropdownId(null);
    } else {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setOpenUpward(spaceBelow < 150);
      setOpenDropdownId(item._id);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return "bg-[#dcfce7] text-[#16a34a]";
      case "scheduled":
        return "bg-[#e0f2fe] text-[#0284c7]";
      case "failed":
        return "bg-[#fee2e2] text-[#dc2626]";
      default:
        return "bg-[#f1f5f9] text-[#64748b]";
    }
  };

  return (
    <tr className="custom-table-row">
      <td className="custom-table-cell ">{startIndex + index + 1}</td>
      <td className="custom-table-cell ">{item.title || "N/A"}</td>
      <td className="custom-table-cell" title={item.message}>
        {item.message || "N/A"}
      </td>
      <td className="custom-table-cell capitalize">{item.send_type}s</td>
      <td className="custom-table-cell text-center">
        <span
          className={`inline-block rounded-[8px] px-3 py-1 text-xs font-semibold capitalize ${getStatusBadge(
            item.status
          )}`}
        >
          {item.status}
        </span>
      </td>
      <td className="custom-table-cell whitespace-nowrap">
        {item.sent_at
          ? new Date(item.sent_at).toLocaleString()
          : item.scheduled_at
          ? new Date(item.scheduled_at).toLocaleString()
          : "-"}
      </td>

      {/* Action Dropdown Menu Cell */}
      <td className="custom-table-cell relative text-center">
        <button onClick={handleToggleMenu} className="action-menu-btn">
          <MdMoreVert className="text-lg" />
        </button>

        {isMenuOpen && (
          <div
            ref={dropdownRef}
            className={`action-dropdown-menu ${
              openUpward ? "menu-bottom" : "menu-top"
            }`}
          >
            {item.status === "sent" ? (
              <button
                onClick={() => {
                  onAskResend(item);
                  setOpenDropdownId(null);
                }}
                className="action-dropdown-item"
              >
                <MdRotateRight className="text-base text-[#02a9e0]" />
                Resend
              </button>
            ) : (
              <span className="block px-3 py-1.5 text-left text-xs text-gray-400">
                No actions available
              </span>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// --- MAIN NOTIFICATIONS COMPONENT ---
export default function PushNotifications() {
  const [data, setData] = useState([]);
  const [alert, setAlert] = useState("");
  const [formAlert, setFormAlert] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    totalNotifications: 0,
    totalPages: 1,
  });

  // Modal & Resend States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resendItem, setResendItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);

  const [selectedOrganizer, setSelectedOrganizer] = useState(
    localStorage.getItem("company_id") || ""
  );

  // Close action dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounce Search Input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Handle Event / Company Listener
  useEffect(() => {
    const handleOrganizerChange = (e) => {
      setSelectedOrganizer(e.detail);
      setPage(1);
    };
    window.addEventListener("companyChanged", handleOrganizerChange);
    return () =>
      window.removeEventListener("companyChanged", handleOrganizerChange);
  }, []);

  // Fetch Notifications Data from Backend
  const fetchData = useCallback(async () => {
    if (!selectedOrganizer) return;
    try {
      setLoading(true);
      const params = {
        company_id: selectedOrganizer,
        page,
        limit,
        search: debouncedSearch,
        status: statusFilter,
      };

      const res = await getNotifications(params);
      const responseData = res.data?.data || res.data || {};

      setData(responseData.notifications || []);
      if (responseData.pagination) {
        setPagination(responseData.pagination);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizer, page, limit, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Form Submission (Create or Schedule)
  const handleFormSubmit = async (formData) => {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        ...formData,
        company_id: selectedOrganizer,
        created_by: userData.userId,
      };

      await createNotification(payload);
      setAlert(
        formData.is_scheduled
          ? "Notification scheduled successfully!"
          : "Notification sent successfully!"
      );

      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to send notification:", error);
      setFormAlert(
        error.response?.data?.message || "Failed to dispatch notification."
      );
    }
  };

  // Confirm Resend Notification
  const handleConfirmResend = async () => {
    if (!resendItem) return;
    try {
      setResendLoading(true);
      await resendNotification(resendItem._id);
      setAlert("Notification resent successfully!");
      setResendItem(null);
      fetchData();
    } catch (error) {
      console.error("Resend error:", error);
    } finally {
      setResendLoading(false);
    }
  };

  const statusOptions = [
    { value: "all", label: "Status: All" },
    { value: "sent", label: "Sent" },
    { value: "scheduled", label: "Scheduled" },
    { value: "failed", label: "Failed" },
  ];

  const startIndex = (page - 1) * limit;

  return (
    <TablePageLayout>
      {/* Top Filter Bar */}
      <div className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col">
          <InputField
            label="SEARCH"
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages..."
            icon={<MdSearch className="text-lg text-gray-400" />}
          />
        </div>

        <div className="flex flex-col">
          <SelectField
            label="STATUS"
            name="status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={statusOptions}
          />
        </div>

        <div className="flex flex-col items-start justify-start gap-2 sm:col-span-2 sm:flex-row sm:justify-end lg:col-span-2">
          <CustomButton
            label="Compose Message"
            icon={MdAddBox}
            iconPos="right"
            variant="solid"
            color="#02a9e0"
            onClick={() => {
              setFormAlert("");
              setIsModalOpen(true);
            }}
          />
        </div>
      </div>

      {alert && (
        <Alert
          type="success"
          message={alert}
          onClose={() => setAlert("")}
          autoClose
        />
      )}

      {/* Table Section */}
      {loading && data.length === 0 ? (
        <div className="rounded-xl border bg-white p-12 text-center text-sm text-gray-500 shadow-sm">
          Loading notifications...
        </div>
      ) : (
        <div className="table-card overflow-hidden">
          <div className="table-scroll-body">
            <table className="custom-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="w-12">#</th>
                  <th>Message Title</th>
                  <th>Content</th>
                  <th>Audience</th>
                  <th className="text-center">Status</th>
                  <th>Sent / Scheduled Date</th>
                  <th className="w-16 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="custom-table-cell py-12 text-center text-gray-500"
                    >
                      No notifications found
                    </td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <NotificationRow
                      key={item._id}
                      item={item}
                      index={index}
                      startIndex={startIndex}
                      onAskResend={setResendItem}
                      openDropdownId={openDropdownId}
                      setOpenDropdownId={setOpenDropdownId}
                      openUpward={openUpward}
                      setOpenUpward={setOpenUpward}
                      dropdownRef={dropdownRef}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages || 1}
            totalEntries={pagination.totalNotifications || 0}
            limit={limit}
            onPageChange={(newPage) => setPage(newPage)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* Compose Notification Modal */}
      <Modal
        open={isModalOpen}
        title="Compose New Notification"
        onClose={() => setIsModalOpen(false)}
      >
        <BulkNotificationForm
          onSubmit={handleFormSubmit}
          alert={formAlert}
          setAlert={setFormAlert}
        />
      </Modal>

      {/* Resend Confirmation Modal */}
      <ConfirmModal
        open={Boolean(resendItem)}
        title="Resend Notification?"
        message={`Are you sure you want to broadcast "${resendItem?.title}" again to ${resendItem?.send_type}s?`}
        confirmText="Confirm Resend"
        loading={resendLoading}
        icon={MdRotateRight}
        iconBgClass="bg-indigo-50 text-indigo-600"
        confirmBtnBg="#4F46E5"
        onConfirm={handleConfirmResend}
        onCancel={() => setResendItem(null)}
      />
    </TablePageLayout>
  );
}
